import Decimal from 'decimal.js';
import type { SettlementDocumentType } from '../document/document-types';
import { documentBankRequired } from '../document/field-policy';
import { calculateDocumentAmounts } from './document-amounts';
import { calculateCustomsValues, parsePackingValue, sumPackingColumn } from './settlement-calculations';
import type { TradeInput } from './schema';
import type { ValidationIssue } from './validation';

export function validateSettlement(trade: TradeInput, type: SettlementDocumentType): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const require = (field: keyof TradeInput, label: string, severity: 'ERROR' | 'WARNING' = 'ERROR') => {
    if (!trade[field]) issues.push({ code: `${type}_${field.toUpperCase()}_MISSING`, path: field, severity, message: `${label}需要补充。` });
  };
  require('authorizedSignature', '授权签字', 'WARNING');
  if (type === 'BENEFICIARY_CERTIFICATE') {
    for (const [field, label] of [['issuingBankName', '开证行'], ['lcNumber', '信用证号'], ['invoiceNumber', '发票号'], ['billOfLadingNumber', '提单号'], ['shipmentDate', '装运日期'], ['certificateStatement', '受益人声明原文']] as const) require(field, label);
    for (const [field, label] of [['issuingBankAddress', '开证行地址'], ['dispatchDays', '寄单天数'], ['dispatchedDocuments', '已寄送单据清单'], ['vessel', '船名']] as const) require(field, label, 'WARNING');
    return issues;
  }
  if (type === 'PACKING_LIST') {
    require('consignee', '收货人 TO');
    require('invoiceNumber', '关联发票号');
  } else {
    if (!trade.buyer.name) issues.push({ code: 'BUYER_REQUIRED', path: 'buyer.name', severity: 'ERROR', message: '买方/进口商 TO 需要补充。' });
    if (!trade.buyer.address) issues.push({ code: 'BUYER_ADDRESS_MISSING', path: 'buyer.address', severity: 'WARNING', message: '买方/进口商地址需要补充。' });
    require('currency', '币种');
    require('countryOfOrigin', '原产国');
    if (documentBankRequired(type, trade.paymentMethod)) require('bankAccountVersionId', '收款银行账户');
    require('incoterm', '贸易条款');
    if (type === 'COMMERCIAL_INVOICE') require('paymentTerms', '付款条件', 'WARNING');
    if (type === 'CUSTOMS_INVOICE') {
      require('destinationCountry', '目的国');
      if (trade.exchangeRate != null && new Decimal(trade.exchangeRate).lte(0)) issues.push({ code: 'EXCHANGE_RATE_INVALID', path: 'exchangeRate', severity: 'ERROR', message: '汇率必须大于零。' });
    }
  }
  if (!trade.items.length) issues.push({ code: 'ITEM_REQUIRED', path: 'items', severity: 'ERROR', message: '至少需要一行商品。' });
  trade.items.forEach((item, index) => {
    const add = (field: string, message: string) => issues.push({ code: `ITEM_${field.toUpperCase()}_INVALID`, path: `items.${index}.${field}`, severity: 'ERROR', message });
    if (!item.name && !item.description) add('name', '品名需要补充。');
    if (item.quantity == null || new Decimal(item.quantity).lte(0)) add('quantity', '数量必须大于零。');
    if (type === 'PACKING_LIST') {
      for (const [field, kind] of [['packages', 'packages'], ['grossWeight', 'weight'], ['netWeight', 'weight'], ['measurement', 'volume']] as const) {
        if (!parsePackingValue(item[field], kind)) add(field, `${field} 需要有效数值；重量用 kg，体积用 m³。`);
      }
      if (!item.cartonNumber) add('cartonNumber', '箱号需要补充。');
      const gross = parsePackingValue(item.grossWeight, 'weight');
      const net = parsePackingValue(item.netWeight, 'weight');
      if (gross && net && net.amount.gt(gross.amount)) add('netWeight', '净重不能大于毛重。');
    } else {
      if (!item.hsCode) add('hsCode', 'HS 编码需要补充。');
      if (!item.unit) add('unit', '单位需要补充。');
      if (item.unitPrice == null) add('unitPrice', '单价需要补充。');
      else if (new Decimal(item.unitPrice).isZero() && !item.freeSampleReason) add('freeSampleReason', '零单价需填写原因。');
    }
  });
  if (type === 'PACKING_LIST') {
    if (trade.items.length && sumPackingColumn(trade.items.map(item => item.packages), 'packages') === null) issues.push({ code: 'PACKAGES_INCOMPLETE', path: 'items.packages', severity: 'ERROR', message: '件数须完整且包装单位一致，不能混合累加箱和托盘。' });
    return issues;
  }
  const amounts = calculateDocumentAmounts(trade, type);
  if (type === 'CUSTOMS_INVOICE') {
    const customs = calculateCustomsValues({ subtotal: amounts.subtotal, incoterm: trade.incoterm, freight: trade.freight, insurance: trade.insurance });
    if (customs.cif === null || new Decimal(customs.cif).lte(0)) issues.push({ code: 'CUSTOMS_VALUE_INVALID', path: 'incoterm', severity: 'ERROR', message: '请补齐 FOB/CFR/CIF、运费和保费；FOB 不能为负，CIF 必须大于零。' });
    if (new Decimal(trade.otherCharges ?? 0).gt(0) || new Decimal(trade.discount ?? 0).gt(0)) issues.push({ code: 'CUSTOMS_ADJUSTMENT_UNSUPPORTED', path: 'otherCharges', severity: 'ERROR', message: '请将其他费用与折扣计入商品成交单价，确保海关金额与发票一致。' });
  } else if (amounts.total === null || new Decimal(amounts.total).lte(0)) {
    issues.push({ code: 'AMOUNT_INCOMPLETE', path: 'total', severity: 'ERROR', message: '请补齐单价和费用，合计必须大于零。' });
  }
  return issues;
}
